import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  MobileStepper,
  Grid,
  Fade,
  Slide,
  InputAdornment,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../contexts/AuthContext';
import { useBudget } from '../../contexts/BudgetContext';
import { useCurrency, CURRENCIES } from '../../contexts/CurrencyContext';
import { useTranslation } from 'react-i18next';

// ─── Constants ──────────────────────────────────────────────
const STORAGE_KEY = 'onboarding_v1_complete';
const TOTAL_STEPS = 5;

const POPULAR_CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'JPY', 'CAD', 'AUD', 'CDF'];

const DEFAULT_BUDGETS = [
  { emoji: '🍕', title: 'Food & Dining', category: 'Food & Dining', amount: 400, color: '#ef4444' },
  { emoji: '🏠', title: 'Housing', category: 'Housing', amount: 800, color: '#3b82f6' },
  { emoji: '🚗', title: 'Transport', category: 'Transport', amount: 200, color: '#f59e0b' },
  { emoji: '🎭', title: 'Entertainment', category: 'Entertainment', amount: 150, color: '#8b5cf6' },
];

const AI_AGENTS = [
  { emoji: '💰', name: 'Budget Guardian', role: 'Tracks spending limits & alerts you before you overspend' },
  { emoji: '🔍', name: 'Receipt Scanner', role: 'Reads & categorizes receipts with AI precision' },
  { emoji: '📊', name: 'Insight Engine', role: 'Predicts spending trends & finds savings opportunities' },
  { emoji: '🛡️', name: 'Security Sentinel', role: 'Protects your account & monitors suspicious activity' },
];

const STEP_GRADIENTS = {
  light: [
    'linear-gradient(135deg, #e0f2fe 0%, #f0fdf4 50%, #fefce8 100%)',
    'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #e0f2fe 100%)',
    'linear-gradient(135deg, #fef3c7 0%, #fef9c3 50%, #ecfdf5 100%)',
    'linear-gradient(135deg, #ede9fe 0%, #e0e7ff 50%, #f0fdf4 100%)',
    'linear-gradient(135deg, #fce7f3 0%, #fef3c7 50%, #e0f2fe 100%)',
  ],
  dark: [
    'linear-gradient(135deg, #0f172a 0%, #1a2332 50%, #0f172a 100%)',
    'linear-gradient(135deg, #0f2419 0%, #0f172a 50%, #1a1a2e 100%)',
    'linear-gradient(135deg, #1a1a0f 0%, #0f172a 50%, #0f2419 100%)',
    'linear-gradient(135deg, #1a0f2e 0%, #0f172a 50%, #0f2419 100%)',
    'linear-gradient(135deg, #2e0f1a 0%, #1a1a0f 50%, #0f172a 100%)',
  ],
};

// ─── CSS Keyframes (injected once) ──────────────────────────
const CONFETTI_STYLES_ID = 'welcome-wizard-confetti-styles';

function injectConfettiStyles() {
  if (document.getElementById(CONFETTI_STYLES_ID)) return;
  const style = document.createElement('style');
  style.id = CONFETTI_STYLES_ID;
  style.textContent = `
    @keyframes confettiFall {
      0%   { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
      100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
    }
    @keyframes confettiPop {
      0%   { transform: scale(0) rotate(0deg); opacity: 0; }
      50%  { transform: scale(1.2) rotate(180deg); opacity: 1; }
      100% { transform: scale(1) rotate(360deg); opacity: 1; }
    }
    @keyframes pulseGlow {
      0%, 100% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.3); }
      50%      { box-shadow: 0 0 40px rgba(16, 185, 129, 0.6); }
    }
    @keyframes floatUp {
      0%   { transform: translateY(40px); opacity: 0; }
      100% { transform: translateY(0); opacity: 1; }
    }
    @keyframes shimmer {
      0%   { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
  `;
  document.head.appendChild(style);
}

// ─── Confetti Overlay ───────────────────────────────────────
const ConfettiOverlay = React.memo(() => {
  const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];
  const pieces = useMemo(() =>
    Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      animDelay: `${Math.random() * 3}s`,
      animDuration: `${2 + Math.random() * 3}s`,
      color: colors[i % colors.length],
      size: 6 + Math.random() * 8,
      shape: Math.random() > 0.5 ? '50%' : '2px',
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 10 }}>
      {pieces.map((p) => (
        <Box
          key={p.id}
          sx={{
            position: 'absolute',
            left: p.left,
            top: '-10px',
            width: p.size,
            height: p.size,
            borderRadius: p.shape,
            backgroundColor: p.color,
            animation: `confettiFall ${p.animDuration} ${p.animDelay} ease-in infinite`,
          }}
        />
      ))}
    </Box>
  );
});
ConfettiOverlay.displayName = 'ConfettiOverlay';

// ─── Step Wrapper (animation) ───────────────────────────────
const StepContent = React.memo(({ active, direction, children }) => (
  <Slide direction={direction} in={active} mountOnEnter unmountOnExit timeout={400}>
    <Fade in={active} timeout={500}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: 560,
          mx: 'auto',
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 3 },
          minHeight: 0,
          flex: 1,
          overflowY: 'auto',
        }}
      >
        {children}
      </Box>
    </Fade>
  </Slide>
));
StepContent.displayName = 'StepContent';

// ═══════════════════════════════════════════════════════════
//  STEP 1 — WELCOME
// ═══════════════════════════════════════════════════════════
const StepWelcome = React.memo(({ userName }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <StepContent active direction="left">
      {/* Animated Logo */}
      <Box
        sx={{
          fontSize: { xs: '4.5rem', sm: '6rem' },
          lineHeight: 1,
          mb: 2,
          animation: 'floatUp 0.8s ease-out',
          filter: 'drop-shadow(0 8px 24px rgba(16,185,129,0.35))',
        }}
      >
        💰
      </Box>

      <Typography
        variant="h3"
        sx={{
          fontWeight: 800,
          mb: 1,
          textAlign: 'center',
          animation: 'floatUp 0.8s ease-out 0.15s both',
          fontSize: { xs: '1.75rem', sm: '2.5rem' },
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {t('onboarding.welcome', 'Welcome')}, {userName || t('onboarding.friend', 'Friend')}!
      </Typography>

      <Typography
        variant="h6"
        sx={{
          textAlign: 'center',
          color: 'text.secondary',
          fontWeight: 400,
          maxWidth: 400,
          lineHeight: 1.6,
          animation: 'floatUp 0.8s ease-out 0.3s both',
          fontSize: { xs: '0.95rem', sm: '1.15rem' },
        }}
      >
        {t(
          'onboarding.welcomeSub',
          "Let's set up your financial command center in 60 seconds"
        )}
      </Typography>

      {/* Decorative feature badges */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mt: 4,
          justifyContent: 'center',
          animation: 'floatUp 0.8s ease-out 0.5s both',
        }}
      >
        {['🎯 Smart Budgets', '📸 Receipt AI', '📊 Insights', '🔒 Secure'].map((label) => (
          <Chip
            key={label}
            label={label}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '0.8rem',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          />
        ))}
      </Box>
    </StepContent>
  );
});
StepWelcome.displayName = 'StepWelcome';

// ═══════════════════════════════════════════════════════════
//  STEP 2 — CURRENCY
// ═══════════════════════════════════════════════════════════
const StepCurrency = React.memo(({ selectedCurrency, onSelect }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [search, setSearch] = useState('');

  const popularList = useMemo(
    () => CURRENCIES.filter((c) => POPULAR_CURRENCIES.includes(c.code)),
    []
  );

  const filteredList = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    return CURRENCIES.filter(
      (c) =>
        !POPULAR_CURRENCIES.includes(c.code) &&
        (c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q))
    ).slice(0, 12);
  }, [search]);

  const displayList = search.trim() ? filteredList : popularList;

  return (
    <StepContent active direction="left">
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 0.5,
          textAlign: 'center',
          animation: 'floatUp 0.6s ease-out',
          fontSize: { xs: '1.5rem', sm: '2rem' },
        }}
      >
        {t('onboarding.currencyTitle', "What's your primary currency?")}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          textAlign: 'center',
          mb: 2.5,
          animation: 'floatUp 0.6s ease-out 0.1s both',
        }}
      >
        {t('onboarding.currencySub', 'We auto-detected yours — change it anytime')}
      </Typography>

      {/* Search */}
      <TextField
        size="small"
        placeholder={t('onboarding.searchCurrency', 'Search currencies...')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2,
          width: '100%',
          maxWidth: 340,
          animation: 'floatUp 0.6s ease-out 0.15s both',
          '& .MuiOutlinedInput-root': { borderRadius: 3 },
        }}
      />

      {/* Currency Grid */}
      <Grid container spacing={1.5} sx={{ maxWidth: 460, animation: 'floatUp 0.6s ease-out 0.2s both' }}>
        {displayList.map((c) => {
          const isSelected = c.code === selectedCurrency;
          return (
            <Grid item xs={6} sm={4} key={c.code}>
              <Box
                onClick={() => onSelect(c.code)}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  cursor: 'pointer',
                  textAlign: 'center',
                  border: `2px solid ${isSelected ? theme.palette.primary.main : alpha(theme.palette.divider, 0.3)}`,
                  bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    transform: 'translateY(-2px)',
                  },
                  position: 'relative',
                }}
              >
                {isSelected && (
                  <CheckCircleIcon
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      fontSize: 18,
                      color: 'primary.main',
                    }}
                  />
                )}
                <Typography sx={{ fontSize: '1.6rem', mb: 0.3 }}>{c.flag}</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{c.code}</Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}
                >
                  {c.symbol} · {c.name}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {search.trim() && filteredList.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
          {t('onboarding.noCurrencyMatch', 'No currencies match your search')}
        </Typography>
      )}
    </StepContent>
  );
});
StepCurrency.displayName = 'StepCurrency';

// ═══════════════════════════════════════════════════════════
//  STEP 3 — QUICK BUDGET SETUP
// ═══════════════════════════════════════════════════════════
const StepBudgets = React.memo(({ budgets, onUpdateAmount, currencySymbol }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <StepContent active direction="left">
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 0.5,
          textAlign: 'center',
          animation: 'floatUp 0.6s ease-out',
          fontSize: { xs: '1.5rem', sm: '2rem' },
        }}
      >
        {t('onboarding.budgetTitle', 'Set up your essential budgets')}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          textAlign: 'center',
          mb: 3,
          animation: 'floatUp 0.6s ease-out 0.1s both',
        }}
      >
        {t('onboarding.budgetSub', 'Starting points — adjust them anytime')}
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 440, animation: 'floatUp 0.6s ease-out 0.2s both' }}>
        {budgets.map((b, idx) => (
          <Box
            key={b.category}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              mb: 1.5,
              borderRadius: 3,
              bgcolor: alpha(b.color, theme.palette.mode === 'dark' ? 0.12 : 0.06),
              border: `1px solid ${alpha(b.color, 0.25)}`,
              animation: `floatUp 0.5s ease-out ${0.15 + idx * 0.08}s both`,
            }}
          >
            <Box
              sx={{
                fontSize: '1.8rem',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2.5,
                bgcolor: alpha(b.color, 0.15),
                flexShrink: 0,
              }}
            >
              {b.emoji}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                {t(`onboarding.budget.${b.category}`, b.title)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('onboarding.monthly', 'Monthly budget')}
              </Typography>
            </Box>

            <TextField
              type="number"
              size="small"
              value={b.amount}
              onChange={(e) => onUpdateAmount(idx, e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.85rem' }}>
                      {currencySymbol}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 120,
                flexShrink: 0,
                '& .MuiOutlinedInput-root': { borderRadius: 2.5 },
                '& input': { textAlign: 'right', fontWeight: 600, fontSize: '0.95rem' },
                '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
                '& input[type=number]': { MozAppearance: 'textfield' },
              }}
            />
          </Box>
        ))}
      </Box>
    </StepContent>
  );
});
StepBudgets.displayName = 'StepBudgets';

// ═══════════════════════════════════════════════════════════
//  STEP 4 — FIRST RECEIPT
// ═══════════════════════════════════════════════════════════
const StepReceipt = React.memo(({ onSkip }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <StepContent active direction="left">
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 0.5,
          textAlign: 'center',
          animation: 'floatUp 0.6s ease-out',
          fontSize: { xs: '1.5rem', sm: '2rem' },
        }}
      >
        {t('onboarding.receiptTitle', 'Snap your first receipt')}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          textAlign: 'center',
          mb: 4,
          maxWidth: 380,
          animation: 'floatUp 0.6s ease-out 0.1s both',
        }}
      >
        {t(
          'onboarding.receiptSub',
          'Our AI reads receipts and categorizes expenses automatically'
        )}
      </Typography>

      {/* Camera / Upload Area */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 320,
          aspectRatio: '4/3',
          borderRadius: 4,
          border: `2px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          cursor: 'pointer',
          transition: 'all 0.25s ease',
          animation: 'floatUp 0.6s ease-out 0.2s both',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            transform: 'translateY(-3px)',
            boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`,
          },
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CameraAltIcon sx={{ fontSize: 36, color: 'primary.main' }} />
        </Box>
        <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
          {t('onboarding.tapToScan', 'Tap to scan a receipt')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          <CloudUploadIcon fontSize="small" />
          <Typography variant="caption">
            {t('onboarding.orUpload', 'or upload from gallery')}
          </Typography>
        </Box>
      </Box>

      {/* Skip prominently */}
      <Button
        variant="text"
        onClick={onSkip}
        sx={{
          mt: 3,
          color: 'text.secondary',
          fontWeight: 600,
          fontSize: '0.95rem',
          textTransform: 'none',
          animation: 'floatUp 0.6s ease-out 0.35s both',
          '&:hover': { color: 'primary.main' },
        }}
      >
        {t('onboarding.skipForNow', 'Skip for now →')}
      </Button>
    </StepContent>
  );
});
StepReceipt.displayName = 'StepReceipt';

// ═══════════════════════════════════════════════════════════
//  STEP 5 — MEET YOUR AI
// ═══════════════════════════════════════════════════════════
const StepAI = React.memo(({ showConfetti }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <StepContent active direction="left">
      {showConfetti && <ConfettiOverlay />}

      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 0.5,
          textAlign: 'center',
          animation: 'floatUp 0.6s ease-out',
          fontSize: { xs: '1.5rem', sm: '2rem' },
        }}
      >
        🎉 {t('onboarding.aiTitle', '12 AI agents are now watching your finances')}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          textAlign: 'center',
          mb: 3,
          animation: 'floatUp 0.6s ease-out 0.1s both',
        }}
      >
        {t('onboarding.aiSub', 'They work 24/7 so you don\'t have to')}
      </Typography>

      <Grid container spacing={2} sx={{ maxWidth: 480, animation: 'floatUp 0.6s ease-out 0.2s both' }}>
        {AI_AGENTS.map((agent, idx) => (
          <Grid item xs={6} key={agent.name}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                textAlign: 'center',
                height: '100%',
                animation: `confettiPop 0.5s ease-out ${0.3 + idx * 0.12}s both`,
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'translateY(-3px)' },
              }}
            >
              <Typography sx={{ fontSize: '2rem', mb: 0.5 }}>{agent.emoji}</Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  mb: 0.3,
                  color: 'primary.main',
                }}
              >
                {t(`onboarding.agent.${agent.name}`, agent.name)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3, display: 'block' }}>
                {t(`onboarding.agentRole.${agent.name}`, agent.role)}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </StepContent>
  );
});
StepAI.displayName = 'StepAI';

// ═══════════════════════════════════════════════════════════
//  MAIN WIZARD
// ═══════════════════════════════════════════════════════════
export default function WelcomeWizard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { user } = useAuth();
  const { handleSaveBudget, syncWithServer, clearErrors } = useBudget();
  const { currency, updateCurrency, getCurrencySymbol } = useCurrency();

  // ── State ─────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState(currency || 'EUR');
  const [budgets, setBudgets] = useState(DEFAULT_BUDGETS);
  const [saving, setSaving] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const hasInitialized = useRef(false);

  // ── Show condition ────────────────────────────────────────
  useEffect(() => {
    if (hasInitialized.current) return;
    const completed = localStorage.getItem(STORAGE_KEY);
    if (!completed && user) {
      setOpen(true);
      injectConfettiStyles();
      hasInitialized.current = true;
    }
  }, [user]);

  // ── Auto-detect currency from browser locale ──────────────
  useEffect(() => {
    if (!open) return;
    try {
      const locale = navigator.language || navigator.languages?.[0] || 'en-US';
      const regionMap = {
        US: 'USD', GB: 'GBP', JP: 'JPY', CH: 'CHF', CA: 'CAD', AU: 'AUD',
        CN: 'CNY', IN: 'INR', BR: 'BRL', CD: 'CDF', FR: 'EUR', DE: 'EUR',
        IT: 'EUR', ES: 'EUR', PT: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR',
        IE: 'EUR', FI: 'EUR', GR: 'EUR',
      };
      const region = locale.split('-')[1]?.toUpperCase();
      const detected = regionMap[region];
      if (detected && CURRENCIES.some((c) => c.code === detected)) {
        setSelectedCurrency(detected);
      }
    } catch {
      // keep default
    }
  }, [open]);

  // ── Handlers ──────────────────────────────────────────────
  const handleClose = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setOpen(false);
  }, []);

  const handleNext = useCallback(async () => {
    if (activeStep === 1) {
      // Save currency on leaving step 2
      try {
        await updateCurrency(selectedCurrency);
      } catch (e) {
        console.warn('Currency update failed:', e);
      }
    }

    if (activeStep === 2) {
      // Save budgets on leaving step 3
      setSaving(true);
      try {
        for (const b of budgets) {
          if (b.amount > 0) {
            await handleSaveBudget({
              title: b.title,
              amount: Number(b.amount),
              category: b.category,
              color: b.color,
              period: 'monthly',
            }, { silent: true });
          }
        }
      } catch (e) {
        console.warn('Budget creation failed:', e);
      }
      setSaving(false);
    }

    if (activeStep < TOTAL_STEPS - 1) {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      if (nextStep === TOTAL_STEPS - 1) {
        setShowConfetti(true);
      }
    }
  }, [activeStep, selectedCurrency, budgets, updateCurrency, handleSaveBudget]);

  const handleBack = useCallback(() => {
    if (activeStep > 0) setActiveStep((s) => s - 1);
  }, [activeStep]);

  const handleFinish = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setOpen(false);
    // Clear any onboarding-era errors so the dashboard starts clean,
    // then re-sync to load whatever budgets were successfully created.
    clearErrors();
    syncWithServer().catch(() => {});
  }, [clearErrors, syncWithServer]);

  const handleUpdateBudgetAmount = useCallback((idx, value) => {
    setBudgets((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], amount: value === '' ? '' : Math.max(0, Number(value)) };
      return next;
    });
  }, []);

  const handleReceiptSkip = useCallback(() => {
    setActiveStep(TOTAL_STEPS - 1);
    setShowConfetti(true);
  }, []);

  // ── Derived ───────────────────────────────────────────────
  const userName = user?.fullName?.split(' ')[0] || user?.fullName || '';
  const currencySymbol = getCurrencySymbol(selectedCurrency) || '€';
  const gradients = theme.palette.mode === 'dark' ? STEP_GRADIENTS.dark : STEP_GRADIENTS.light;

  const isLastStep = activeStep === TOTAL_STEPS - 1;
  const nextLabel = useMemo(() => {
    if (saving) return t('onboarding.saving', 'Saving...');
    if (isLastStep) return t('onboarding.getStarted', '🚀 Get Started');
    if (activeStep === 3) return t('onboarding.next', 'Next');
    return t('onboarding.next', 'Next');
  }, [saving, isLastStep, activeStep, t]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      fullScreen
      PaperProps={{
        sx: {
          background: gradients[activeStep] || gradients[0],
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          // Desktop: slight inset with rounded corners
          ...(!isMobile && {
            m: 3,
            borderRadius: 4,
            maxWidth: 640,
            maxHeight: '90vh',
            mx: 'auto',
          }),
        },
      }}
      sx={{
        zIndex: theme.zIndex.modal + 10,
        '& .MuiBackdrop-root': {
          backgroundColor: alpha('#000', 0.7),
          backdropFilter: 'blur(8px)',
        },
      }}
    >
      {/* ─── Header ───────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          pt: { xs: 1.5, sm: 2 },
          pb: 0.5,
          flexShrink: 0,
        }}
      >
        {/* Step indicator */}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {activeStep + 1} / {TOTAL_STEPS}
        </Typography>

        {/* Skip button */}
        <Button
          size="small"
          onClick={handleClose}
          startIcon={<CloseIcon sx={{ fontSize: '1rem !important' }} />}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            minWidth: 'auto',
            '&:hover': { color: 'text.primary' },
          }}
        >
          {t('onboarding.skip', 'Skip')}
        </Button>
      </Box>

      {/* ─── Content ──────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 0,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {activeStep === 0 && <StepWelcome userName={userName} />}
        {activeStep === 1 && (
          <StepCurrency selectedCurrency={selectedCurrency} onSelect={setSelectedCurrency} />
        )}
        {activeStep === 2 && (
          <StepBudgets
            budgets={budgets}
            onUpdateAmount={handleUpdateBudgetAmount}
            currencySymbol={currencySymbol}
          />
        )}
        {activeStep === 3 && <StepReceipt onSkip={handleReceiptSkip} />}
        {activeStep === 4 && <StepAI showConfetti={showConfetti} />}
      </Box>

      {/* ─── Footer ───────────────────────────────────────── */}
      <Box
        sx={{
          px: 2,
          pb: { xs: 2, sm: 3 },
          pt: 1,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {/* Navigation buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            width: '100%',
            maxWidth: 440,
          }}
        >
          {activeStep > 0 && (
            <Button
              variant="outlined"
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              sx={{
                flex: isMobile ? 1 : 'none',
                height: 48,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: alpha(theme.palette.divider, 0.3),
                color: 'text.secondary',
                '&:hover': { borderColor: theme.palette.primary.main },
              }}
            >
              {t('onboarding.back', 'Back')}
            </Button>
          )}

          <Button
            variant="contained"
            onClick={isLastStep ? handleFinish : handleNext}
            disabled={saving}
            endIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : !isLastStep ? (
                <ArrowForwardIcon />
              ) : null
            }
            sx={{
              flex: 1,
              height: 48,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              bgcolor: 'primary.main',
              color: '#fff',
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              '&:hover': {
                bgcolor: 'primary.dark',
                boxShadow: `0 6px 28px ${alpha(theme.palette.primary.main, 0.5)}`,
              },
              ...(isLastStep && {
                animation: 'pulseGlow 2s ease-in-out infinite',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                },
              }),
            }}
          >
            {nextLabel}
          </Button>
        </Box>

        {/* Progress dots */}
        <MobileStepper
          variant="dots"
          steps={TOTAL_STEPS}
          position="static"
          activeStep={activeStep}
          backButton={null}
          nextButton={null}
          sx={{
            bgcolor: 'transparent',
            justifyContent: 'center',
            p: 0,
            '& .MuiMobileStepper-dot': {
              width: 8,
              height: 8,
              mx: 0.5,
              bgcolor: alpha(theme.palette.text.primary, 0.2),
              transition: 'all 0.3s ease',
            },
            '& .MuiMobileStepper-dotActive': {
              width: 24,
              borderRadius: 4,
              bgcolor: 'primary.main',
            },
          }}
        />
      </Box>
    </Dialog>
  );
}
